import FlightLandIcon from '@mui/icons-material/FlightLand';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import NotesIcon from '@mui/icons-material/Notes';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { convertDecimalToDMS, DMSLocationInput, FlightInfo, GeoValidator, OrderInfo, PortValidator } from 'adoms-common-lib';
import React, { useEffect } from "react";
import { APIConnector } from '../../connector/APIConnector';
import { CoordinateInputModeButtonGroup } from '../atoms/CoordinateInputModeButtonGroup';
import { DmsCoordinateInput } from '../atoms/DmsCoordinateInput';
import { LoadingMark } from '../atoms/LoadingMark';

type FlightInfoEditDialogProps = {
    isFlightInfoEditDialogOpen: boolean
    setFlightInfoEditDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
    selectedFlightInfo: FlightInfo
    setSavedFlightInfo: React.Dispatch<React.SetStateAction<boolean>>
    orderInfoList?: OrderInfo[]
}

type CoordinateInputMode = 'decimal' | 'dms'

/**
 * フライト情報変更
 * @param props 
 * @returns 
 */
export const FlightInfoEditDialog: React.FC<FlightInfoEditDialogProps> = (props) => {

    const isDepartureCoordinateEditable = props.selectedFlightInfo?.departure?.isMobile ?? false;
    const isArrivalCoordinateEditable = props.selectedFlightInfo?.arrival?.isMobile ?? false;

    const [etd, setEtd] = React.useState<string | undefined>(props.selectedFlightInfo?.etd);
    const [eta, setEta] = React.useState<string | undefined>(props.selectedFlightInfo?.eta);
    const [remarks, setRemarks] = React.useState<string | undefined>(props.selectedFlightInfo?.remarks);
    const [unsafeEvent, setUnsafeEvent] = React.useState<string | undefined>(props.selectedFlightInfo?.unsafeEvent ?? "");
    const [departureLatitude, setDepartureLatitude] = React.useState<string | undefined>(props.selectedFlightInfo?.departure.latitude);
    const [departureLongitude, setDepartureLongitude] = React.useState<string | undefined>(props.selectedFlightInfo?.departure.longitude);
    const [arrivalLatitude, setArrivalLatitude] = React.useState<string | undefined>(props.selectedFlightInfo?.arrival.latitude);
    const [arrivalLongitude, setArrivalLongitude] = React.useState<string | undefined>(props.selectedFlightInfo?.arrival.longitude);
    const [departureLatitudeDms, setDepartureLatitudeDms] = React.useState<DMSLocationInput>(toDmsInput(props.selectedFlightInfo?.departure.latitude, true));
    const [departureLongitudeDms, setDepartureLongitudeDms] = React.useState<DMSLocationInput>(toDmsInput(props.selectedFlightInfo?.departure.longitude, false));
    const [arrivalLatitudeDms, setArrivalLatitudeDms] = React.useState<DMSLocationInput>(toDmsInput(props.selectedFlightInfo?.arrival.latitude, true));
    const [arrivalLongitudeDms, setArrivalLongitudeDms] = React.useState<DMSLocationInput>(toDmsInput(props.selectedFlightInfo?.arrival.longitude, false));
    const [departureCoordinateInputMode, setDepartureCoordinateInputMode] = React.useState<CoordinateInputMode>('dms');
    const [arrivalCoordinateInputMode, setArrivalCoordinateInputMode] = React.useState<CoordinateInputMode>('dms');
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const [directChangeEta, setDirectChangeEta] = React.useState(false);
    const [selectedFlightId, setSelectedFlightId] = React.useState<string | undefined>(undefined);
    const [isDisplayLoadingMark, setDisplayLoadingMark] = React.useState<boolean>(false);
    const [isNotifyCustomerETAETDChanged, setNotifyCustomerETAETDChanged] = React.useState<boolean>(false);

    useEffect(() => {
        setSelectedFlightId(props.selectedFlightInfo.id);
        if (selectedFlightId !== props.selectedFlightInfo.id) {
            setDefaultValue();
        };
    }, [props.selectedFlightInfo]);

    useEffect(() => {
        const coordinateErrorMessage = getCoordinateErrorMessage();
        if (coordinateErrorMessage) {
            setErrorMessage(coordinateErrorMessage);
        } else if (errorMessage?.includes('ポートの緯度') || errorMessage?.includes('ポートの経度')) {
            setErrorMessage(undefined);
        }
    }, [departureLatitude, departureLongitude, arrivalLatitude, arrivalLongitude, departureLatitudeDms, departureLongitudeDms, arrivalLatitudeDms, arrivalLongitudeDms, departureCoordinateInputMode, arrivalCoordinateInputMode]);

    const handleFlightInfoEditDialogClose = () => {
        setDefaultValue();
        setDirectChangeEta(false);
        props.setFlightInfoEditDialogOpen(false);
    };

    const setDefaultValue = () => {
        setErrorMessage(undefined);
        setEtd(props.selectedFlightInfo?.etd);
        setEta(props.selectedFlightInfo?.eta);
        setNotifyCustomerETAETDChanged(false);
        setRemarks(props.selectedFlightInfo?.remarks);
        setUnsafeEvent(props.selectedFlightInfo?.unsafeEvent ?? "");
        setDepartureLatitude(props.selectedFlightInfo?.departure.latitude);
        setDepartureLongitude(props.selectedFlightInfo?.departure.longitude);
        setArrivalLatitude(props.selectedFlightInfo?.arrival.latitude);
        setArrivalLongitude(props.selectedFlightInfo?.arrival.longitude);
        setDepartureLatitudeDms(toDmsInput(props.selectedFlightInfo?.departure.latitude, true));
        setDepartureLongitudeDms(toDmsInput(props.selectedFlightInfo?.departure.longitude, false));
        setArrivalLatitudeDms(toDmsInput(props.selectedFlightInfo?.arrival.latitude, true));
        setArrivalLongitudeDms(toDmsInput(props.selectedFlightInfo?.arrival.longitude, false));
        setDepartureCoordinateInputMode('dms');
        setArrivalCoordinateInputMode('dms');
    }

    const disabledSaveButton = (): boolean => {
        const coordinateErrorMessage = getCoordinateErrorMessage();
        if (coordinateErrorMessage) {
            return true;
        }

        if (etd === props.selectedFlightInfo.etd
            && eta === props.selectedFlightInfo.eta
            && remarks === props.selectedFlightInfo.remarks
            && departureLatitude === props.selectedFlightInfo.departure.latitude
            && departureLongitude === props.selectedFlightInfo.departure.longitude
            && arrivalLatitude === props.selectedFlightInfo.arrival.latitude
            && arrivalLongitude === props.selectedFlightInfo.arrival.longitude
            && (unsafeEvent === props.selectedFlightInfo.unsafeEvent
                || (!unsafeEvent && !props.selectedFlightInfo.unsafeEvent))) {
            return true;
        } else {
            let flightDate = props.selectedFlightInfo.flightDate.replace(/-/g, "/");
            if (etd && eta
                && (new Date(flightDate + " " + etd) <= new Date(flightDate + " " + eta))) {
                return false;
            } else {
                return true;
            }
        };
    };

    const disabledResetButton = (): boolean => {
        if (etd === props.selectedFlightInfo.etd
            && eta === props.selectedFlightInfo.eta
            && remarks === props.selectedFlightInfo.remarks
            && departureLatitude === props.selectedFlightInfo.departure.latitude
            && departureLongitude === props.selectedFlightInfo.departure.longitude
            && arrivalLatitude === props.selectedFlightInfo.arrival.latitude
            && arrivalLongitude === props.selectedFlightInfo.arrival.longitude
            && (unsafeEvent === props.selectedFlightInfo.unsafeEvent
                || (!unsafeEvent && !props.selectedFlightInfo.unsafeEvent))) {
            return true;
        } else {
            return false;
        };
    };

    const handleSaveFlightInfo = async () => {
        const coordinateErrorMessage = getCoordinateErrorMessage();
        if (coordinateErrorMessage) {
            setErrorMessage(coordinateErrorMessage);
            return;
        }

        if (props.selectedFlightInfo
            && props.selectedFlightInfo.std
            && props.selectedFlightInfo.sta
            && etd
            && eta) {
            setDisplayLoadingMark(true);
            setErrorMessage(undefined);

            let flightScheduleChangeParam = {
                id: props.selectedFlightInfo.id,
                std: props.selectedFlightInfo.std,
                sta: props.selectedFlightInfo.sta,
                etd: etd,
                eta: eta,
                isNotifyCustomerETAETDChanged: isNotifyCustomerETAETDChanged,
                remarks: remarks ?? "",
                unsafeEvent: unsafeEvent ?? undefined,
                departureLatitude: departureCoordinateInputMode === 'decimal'
                    ? formatDecimalCoordinateForSave(departureLatitude ?? props.selectedFlightInfo.departure.latitude)
                    : departureLatitude ?? props.selectedFlightInfo.departure.latitude,
                departureLongitude: departureCoordinateInputMode === 'decimal'
                    ? formatDecimalCoordinateForSave(departureLongitude ?? props.selectedFlightInfo.departure.longitude)
                    : departureLongitude ?? props.selectedFlightInfo.departure.longitude,
                arrivalLatitude: arrivalCoordinateInputMode === 'decimal'
                    ? formatDecimalCoordinateForSave(arrivalLatitude ?? props.selectedFlightInfo.arrival.latitude)
                    : arrivalLatitude ?? props.selectedFlightInfo.arrival.latitude,
                arrivalLongitude: arrivalCoordinateInputMode === 'decimal'
                    ? formatDecimalCoordinateForSave(arrivalLongitude ?? props.selectedFlightInfo.arrival.longitude)
                    : arrivalLongitude ?? props.selectedFlightInfo.arrival.longitude,
                report: props.selectedFlightInfo.report
            };

            let apiConnector: APIConnector = APIConnector.instance;
            await apiConnector.putFlightScheduleChange(flightScheduleChangeParam).then(async value => {
                props.setSavedFlightInfo(true);
                props.setFlightInfoEditDialogOpen(false);
                setDirectChangeEta(false);
                setDisplayLoadingMark(false);
            }).catch(error => {
                console.log(error);
                setErrorMessage("フライト情報を更新できませんでした。");
                setDisplayLoadingMark(false);
            });
        };
    };

    const getCoordinateErrorMessage = (): string | undefined => {
        const validateCoordinate = (
            title: string,
            latitude?: string,
            longitude?: string,
            latitudeDms?: DMSLocationInput,
            longitudeDms?: DMSLocationInput,
            coordinateInputMode?: CoordinateInputMode,
        ): string | undefined => {
            if (coordinateInputMode === 'dms') {
                const latitudeDmsResult = parseDmsCoordinate(latitudeDms, true);
                if (latitudeDmsResult.errorMessage) {
                    return `${title}の緯度(60進法)： ${latitudeDmsResult.errorMessage}`;
                }
                const longitudeDmsResult = parseDmsCoordinate(longitudeDms, false);
                if (longitudeDmsResult.errorMessage) {
                    return `${title}の経度(60進法)： ${longitudeDmsResult.errorMessage}`;
                }
            }
            if (typeof latitude === 'undefined' || typeof longitude === 'undefined') {
                return undefined;
            }
            if (!PortValidator.isLatLonFormat(latitude)) {
                return `${title}の緯度の形式が不正です。`;
            }
            if (!PortValidator.isLatLonFormat(longitude)) {
                return `${title}の経度の形式が不正です。`;
            }
            if (!GeoValidator.checkLat(Number(latitude)).result) {
                return `${title}の緯度は-90〜90で入力してください。`;
            }
            if (!GeoValidator.checkLon(Number(longitude)).result) {
                return `${title}の経度は-180〜180で入力してください。`;
            }
            return undefined;
        };

        if (isDepartureCoordinateEditable) {
            const departureError = validateCoordinate('出発ポート', departureLatitude, departureLongitude, departureLatitudeDms, departureLongitudeDms, departureCoordinateInputMode);
            if (departureError) {
                return departureError;
            }
        }

        if (isArrivalCoordinateEditable) {
            return validateCoordinate('到着ポート', arrivalLatitude, arrivalLongitude, arrivalLatitudeDms, arrivalLongitudeDms, arrivalCoordinateInputMode);
        }

        return undefined;
    }

    const formatDecimalCoordinate = (value: number): string => {
        const rounded = value.toFixed(6);
        return rounded.replace(/\.?0+$/, '');
    }

    const formatDecimalCoordinateForSave = (value?: string): string | undefined => {
        if (typeof value === 'undefined') {
            return undefined;
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
            return value;
        }

        return numericValue.toFixed(6);
    }

    const normalizeDirection = (value: string): string => {
        const trimmed = value.trim();
        if (trimmed === '') {
            return '';
        }

        const map: { [key: string]: string } = {
            N: 'N',
            S: 'S',
            E: 'E',
            W: 'W',
        };

        const firstChar = trimmed[0].toUpperCase();
        return map[firstChar] ?? firstChar;
    }

    const parseDmsCoordinate = (input: DMSLocationInput | undefined, isLatitude: boolean): { decimal?: number, errorMessage?: string } => {
        if (!input) {
            return {};
        }

        const degreesText = input.degrees.trim();
        const minutesText = input.minutes.trim();
        const secondsText = input.seconds.trim();
        const directionText = normalizeDirection(input.direction);

        if (!degreesText && !minutesText && !secondsText && !directionText) {
            return {};
        }

        if (!degreesText || !minutesText || !secondsText || !directionText) {
            return { errorMessage: '度・分・秒・方位をすべて入力してください。' };
        }

        if (isLatitude && !['N', 'S'].includes(directionText)) {
            return { errorMessage: '緯度の方位はNまたはSで入力してください。' };
        }
        if (!isLatitude && !['E', 'W'].includes(directionText)) {
            return { errorMessage: '経度の方位はEまたはWで入力してください。' };
        }

        const degrees = Number(degreesText);
        const minutes = Number(minutesText);
        const seconds = Number(secondsText);

        if (!Number.isFinite(degrees) || !Number.isFinite(minutes) || !Number.isFinite(seconds)) {
            return { errorMessage: '数値として解釈できません。' };
        }
        if (minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) {
            return { errorMessage: '分秒は0以上60未満で入力してください。' };
        }

        const absDegrees = Math.abs(degrees);
        let decimal = absDegrees + (minutes / 60) + (seconds / 3600);

        const isNegativeByDirection = directionText === 'S' || directionText === 'W';
        const isPositiveByDirection = directionText === 'N' || directionText === 'E';
        if (degrees < 0 && isPositiveByDirection) {
            return { errorMessage: '符号と方位が矛盾しています。' };
        }

        if (isNegativeByDirection || degrees < 0) {
            decimal *= -1;
        }

        if (isLatitude && !GeoValidator.checkLat(decimal).result) {
            return { errorMessage: '緯度の範囲外です。' };
        }
        if (!isLatitude && !GeoValidator.checkLon(decimal).result) {
            return { errorMessage: '経度の範囲外です。' };
        }

        return { decimal };
    }

    function createEmptyDmsInput(): DMSLocationInput {
        return {
            degrees: '',
            minutes: '',
            seconds: '',
            direction: '',
        };
    }

    function toDmsInput(decimalText: string | undefined, isLatitude: boolean): DMSLocationInput {
        if (!decimalText || !PortValidator.isLatLonFormat(decimalText)) {
            return createEmptyDmsInput();
        }

        const decimalValue = Number(decimalText);
        if (!Number.isFinite(decimalValue)) {
            return createEmptyDmsInput();
        }

        const dms = convertDecimalToDMS(decimalValue);
        const absoluteDmsDegrees = Math.abs(dms.degrees);
        const minutes = dms.minutes.toString().padStart(2, '0');
        const seconds = dms.seconds.toFixed(2);
        const direction = isLatitude
            ? (decimalValue >= 0 ? 'N' : 'S')
            : (decimalValue >= 0 ? 'E' : 'W');

        return {
            degrees: String(absoluteDmsDegrees),
            minutes: minutes,
            seconds: seconds,
            direction: direction,
        };
    }

    const handleChangeDecimalCoordinate = (
        value: string,
        isLatitude: boolean,
        setDecimalValue: React.Dispatch<React.SetStateAction<string | undefined>>,
        setDmsValue: React.Dispatch<React.SetStateAction<DMSLocationInput>>,
    ) => {
        setDecimalValue(value);
        if (value.trim() === '') {
            setDmsValue(createEmptyDmsInput());
            return;
        }

        if (PortValidator.isLatLonFormat(value)) {
            const numericValue = Number(value);
            const isInRange = isLatitude
                ? GeoValidator.checkLat(numericValue).result
                : GeoValidator.checkLon(numericValue).result;
            if (isInRange) {
                setDmsValue(toDmsInput(value, isLatitude));
            }
        }
    }

    const handleChangeDmsCoordinateField = (
        value: string,
        field: keyof DMSLocationInput,
        isLatitude: boolean,
        dmsValue: DMSLocationInput,
        setDmsValue: React.Dispatch<React.SetStateAction<DMSLocationInput>>,
        setDecimalValue: React.Dispatch<React.SetStateAction<string | undefined>>,
    ) => {
        const nextDmsValue = {
            ...dmsValue,
            [field]: field === 'direction' ? normalizeDirection(value) : value,
        };
        setDmsValue(nextDmsValue);

        const parseResult = parseDmsCoordinate(nextDmsValue, isLatitude);
        if (typeof parseResult.decimal !== 'undefined') {
            setDecimalValue(formatDecimalCoordinate(parseResult.decimal));
        }
    }

    const flightCoordinateGrid = (
        icon: JSX.Element,
        title: string,
        latitude?: string,
        longitude?: string,
        latitudeDms?: DMSLocationInput,
        longitudeDms?: DMSLocationInput,
        onChangeLatitude?: React.Dispatch<React.SetStateAction<string | undefined>>,
        onChangeLongitude?: React.Dispatch<React.SetStateAction<string | undefined>>,
        onChangeLatitudeDms?: React.Dispatch<React.SetStateAction<DMSLocationInput>>,
        onChangeLongitudeDms?: React.Dispatch<React.SetStateAction<DMSLocationInput>>,
        coordinateInputMode?: CoordinateInputMode,
        onChangeCoordinateInputMode?: React.Dispatch<React.SetStateAction<CoordinateInputMode>>,
    ) => {
        const dmsTextFieldSx = {
            '& .MuiInputBase-input': {
                fontSize: '0.95rem',
                padding: '8px 8px',
            },
            '& .MuiInputLabel-root': {
                fontSize: '0.85rem',
            },
        };

        return (
            <Grid item container spacing={2} direction={"row"} flexWrap="nowrap">
                <Grid item>
                    {icon}
                </Grid>
                <Grid item container direction={"column"} spacing={2}>
                    <Grid item container alignItems="center" justifyContent="space-between">
                        <Grid item>
                            <Typography color="textSecondary">{title}</Typography>
                        </Grid>
                        <Grid item>
                            <CoordinateInputModeButtonGroup
                                ariaLabel={`${title}入力モード`}
                                mode={coordinateInputMode ?? 'dms'}
                                onChangeMode={(mode) => onChangeCoordinateInputMode && onChangeCoordinateInputMode(mode)}
                            />
                        </Grid>
                    </Grid>
                    {coordinateInputMode === 'dms' ?
                        <Grid item container spacing={0.5} direction={"row"}>
                            <Grid item xs={6}>
                                <DmsCoordinateInput
                                    caption="緯度"
                                    value={latitudeDms ?? createEmptyDmsInput()}
                                    directionHelper="N/S"
                                    textFieldSx={dmsTextFieldSx}
                                    onChangeField={(field, value) => handleChangeDmsCoordinateField(value, field, true, latitudeDms ?? createEmptyDmsInput(), onChangeLatitudeDms ?? (() => undefined), onChangeLatitude ?? (() => undefined))}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DmsCoordinateInput
                                    caption="経度"
                                    value={longitudeDms ?? createEmptyDmsInput()}
                                    directionHelper="E/W"
                                    textFieldSx={dmsTextFieldSx}
                                    onChangeField={(field, value) => handleChangeDmsCoordinateField(value, field, false, longitudeDms ?? createEmptyDmsInput(), onChangeLongitudeDms ?? (() => undefined), onChangeLongitude ?? (() => undefined))}
                                />
                            </Grid>
                        </Grid>
                        : <Grid item container spacing={1} direction={"row"}>
                            <Grid item xs={6}>
                                <TextField
                                    label="緯度"
                                    value={latitude}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    onChange={event => handleChangeDecimalCoordinate(event.target.value, true, onChangeLatitude ?? (() => undefined), onChangeLatitudeDms ?? (() => undefined))}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="経度"
                                    value={longitude}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    onChange={event => handleChangeDecimalCoordinate(event.target.value, false, onChangeLongitude ?? (() => undefined), onChangeLongitudeDms ?? (() => undefined))}
                                />
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>
        )
    };

    const flightInfoTextFieldGrid = (icon: JSX.Element, title: string, onChange: React.Dispatch<React.SetStateAction<string | undefined>>, value?: string) => {
        return (
            <Grid item container spacing={2} direction={"row"} flexWrap="nowrap">
                <Grid item>
                    {icon}
                </Grid>
                <Grid item container direction={"column"} spacing={1}>
                    <Grid item>
                        <Typography color="textSecondary">{title}</Typography>
                    </Grid>
                    <Grid item >
                        <TextField
                            value={value}
                            multiline
                            maxRows="10"
                            variant="outlined"
                            fullWidth
                            onChange={event => onChange(event.target.value)}
                        />
                    </Grid>
                </Grid>
            </Grid>
        )
    };

    const handleChangeEtd = (event: any) => {
        let inputEtd = event.target.value;
        setEtd(event.target.value);

        //iPhoneでは日付にハイフンを使えないためスラッシュに置き換える
        let flightDate = props.selectedFlightInfo.flightDate.replace(/-/g, "/");
        let newEtd = new Date(flightDate + ' ' + inputEtd);
        let newEta = new Date(flightDate + ' ' + props.selectedFlightInfo.eta);

        if (!directChangeEta) {
            //出発時刻のみ入力した場合、到着時刻も変更する
            let oldEtd = new Date(flightDate + ' ' + props.selectedFlightInfo.etd);

            let diff = (newEtd.getTime() - oldEtd.getTime()) / (60 * 1000);
            newEta.setMinutes(newEta.getMinutes() + diff);
            let newEtaString = String(String('0' + newEta.getHours()).slice(-2)
                + ':' + String('0' + newEta.getMinutes()).slice(-2));

            setEta(newEtaString);
        } else {
            if (newEtd > newEta) {
                setErrorMessage("ETDよりETAが後の時間になるように設定してください。")
            } else {
                setErrorMessage(undefined);
            };
        };
    };

    const handleChangeEta = (event: any) => {
        let inputEta = event.target.value;
        //iPhoneでは日付にハイフンを使えないためスラッシュに置き換える
        let flightDate = props.selectedFlightInfo.flightDate.replace(/-/g, "/");
        let newEtd = new Date(flightDate + ' ' + props.selectedFlightInfo.etd);
        let newEta = new Date(flightDate + ' ' + inputEta);

        if (newEtd > newEta) {
            setErrorMessage("ETDよりETAが後の時間になるように設定してください。")
        } else {
            setErrorMessage(undefined);
        };

        setDirectChangeEta(true);
        setEta(inputEta);
    };

    return (
        <Dialog
            open={props.isFlightInfoEditDialogOpen}
            onClose={handleFlightInfoEditDialogClose}
        >
            <DialogTitle id="dialog-title">
                フライト情報変更
            </DialogTitle>
            <DialogContent sx={{ paddingBottom: 0, overflow: 'hidden' }}>
                {errorMessage ?
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box> : undefined
                }
                <Box sx={{ maxHeight: '58vh', overflowY: 'auto', pr: 1 }}>
                    <Grid container spacing={2}>
                        <Grid item container spacing={2} direction={"row"} flexWrap="nowrap">
                            <Grid item>
                                <ScheduleIcon sx={{ color: "#797979" }} />
                            </Grid>
                            <Grid item container direction={"column"}>
                                <Grid item>
                                    <Typography width="120px" color="textSecondary">ETD - ETA</Typography>
                                </Grid>
                                <Grid item container direction={"row"} spacing={1}>
                                    <Grid item>
                                        <TextField
                                            variant="standard"
                                            type="time"
                                            value={etd}
                                            onChange={handleChangeEtd} />
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1" style={{ marginTop: "5px", display: "inline-block" }}> - </Typography>
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            variant="standard"
                                            type="time"
                                            value={eta}
                                            onChange={handleChangeEta} />
                                    </Grid>
                                </Grid>
                                {props.orderInfoList != null && props.orderInfoList.length > 0 ?
                                    <Grid item>
                                        <FormControlLabel
                                            control={<Checkbox
                                                checked={isNotifyCustomerETAETDChanged}
                                                onChange={(e) => { setNotifyCustomerETAETDChanged(e.target.checked) }}
                                            />}
                                            label="お客様に通知する" />
                                    </Grid> : undefined
                                }
                            </Grid>
                        </Grid>
                        {flightInfoTextFieldGrid(
                            <WarningAmberOutlinedIcon sx={{ color: "#797979" }} />,
                            "飛行の安全に影響のあった事項",
                            setUnsafeEvent,
                            unsafeEvent
                        )}
                        {isDepartureCoordinateEditable ?
                            flightCoordinateGrid(
                                <FlightTakeoffIcon sx={{ color: "#797979" }} />,
                                "出発ポート座標",
                                departureLatitude,
                                departureLongitude,
                                departureLatitudeDms,
                                departureLongitudeDms,
                                setDepartureLatitude,
                                setDepartureLongitude,
                                setDepartureLatitudeDms,
                                setDepartureLongitudeDms,
                                departureCoordinateInputMode,
                                setDepartureCoordinateInputMode
                            ) : undefined}
                        {isArrivalCoordinateEditable ?
                            flightCoordinateGrid(
                                <FlightLandIcon sx={{ color: "#797979" }} />,
                                "到着ポート座標",
                                arrivalLatitude,
                                arrivalLongitude,
                                arrivalLatitudeDms,
                                arrivalLongitudeDms,
                                setArrivalLatitude,
                                setArrivalLongitude,
                                setArrivalLatitudeDms,
                                setArrivalLongitudeDms,
                                arrivalCoordinateInputMode,
                                setArrivalCoordinateInputMode
                            ) : undefined}
                        {flightInfoTextFieldGrid(
                            <NotesIcon sx={{ color: "#797979" }} />,
                            "備考",
                            setRemarks,
                            remarks
                        )}
                    </Grid>
                </Box>
                <LoadingMark isDisplayLoadingMark={isDisplayLoadingMark} />
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={disabledResetButton()}
                    onClick={setDefaultValue}>
                    リセット
                </Button>
                <Button
                    color="secondary"
                    disabled={disabledSaveButton()}
                    onClick={handleSaveFlightInfo}>
                    登録
                </Button>
            </DialogActions>
        </Dialog >
    )
}